import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
// Select that stays attached to its field while the page scrolls.
import Select from './SheetSelect';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';

// ant design icons
import CloseOutlined from '@ant-design/icons/CloseOutlined';

// project imports
import MainCard from 'components/MainCard';
import HistoryPagination, { HistoryEmptyState } from 'components/HistoryPagination';
import { HISTORY_PAGE_SIZE } from 'hooks/useDatasheetHistory';
import { FORM_TYPES } from 'utils/offlineFirstDataLayer';
import { loadSheetRecords, getLocalSessionEntry } from 'utils/dailyActivityOffline';
import { fetchServiceGoals } from 'utils/iepServiceGoals';
import SpeechTextField from 'components/SpeechTextField';
import DailyActivityGoalRows from './DailyActivityGoalRows';
// The SAME five controlling-prompt codes the Cold Probe / Multi-Trial sheets
// use, from the one shared list, so a prompt level means one thing product-wide
// and saved data reads the same whichever sheet wrote it.
import { PROMPT_LEVELS, toPromptCodeArray } from 'utils/promptLevels';

/* The master OT area list offered in the Area combo.
 *
 * 'cog' and 'sc' are deliberately ABSENT here but still carry translations, because
 * entries recorded before this list was set use them: dropping the label too would
 * render those rows as a raw "sc". They are readable, just no longer offered. A
 * therapist who needs one types it, and it is kept as a custom area on that sheet. */
const AREA_KEYS = ['fm', 'gm', 'vm', 'vp', 'si', 'sr', 'mp', 'bil', 'adl', 'hw', 'play'];

/* The assistance scale, which is FIXED — unlike area and goal it cannot be added to
 * inline. How much help a child needed is a clinical scale, so changing it is a
 * configuration decision rather than something a therapist does mid-session. */
const PERF_KEYS = ['i', 'minA', 'modA', 'maxA', 'd'];

const PERF_COLOR = {
  i: 'success',
  minA: 'info',
  modA: 'warning',
  maxA: 'error',
  d: 'default'
};

const DATASHEET_TYPE_CODE = 'ot_data_sheet';

/* The service this sheet belongs to (clinical.ref_service.code). The child's IEP
 * goals tagged with it seed the table, so the therapist starts from the plan
 * rather than retyping it. */
const SERVICE_CODE = 'occupational_therapy';

// Activities may arrive as a legacy single string or the current array. Normalise
// to an array of non-empty strings so the expandable list always has a clean shape.
const normalizeActivities = (a) => {
  if (Array.isArray(a)) return a.map((x) => String(x || '').trim()).filter(Boolean);
  const s = String(a || '').trim();
  return s ? [s] : [];
};

const OTDataSheetMode = ({ onDataChange, childId, childName = '', therapistId, therapistName = '', sessionId = null, child = null }) => {
  const intl = useIntl();
  const theme = useTheme();

  const AREA_OPTIONS = useMemo(
    () => AREA_KEYS.map((k) => ({ key: k, label: intl.formatMessage({ id: `form.otDataSheet.area.${k}` }) })),
    [intl]
  );
  const PERF_OPTIONS = useMemo(
    () => PERF_KEYS.map((k) => ({ value: k, label: intl.formatMessage({ id: `form.otDataSheet.perf.${k}` }) })),
    [intl]
  );

  const [tabValue, setTabValue] = useState(0);
  // goals: [{ id, area, goal, activities: string[], performance, expanded }]
  const [goals, setGoals] = useState([]);
  const [notes, setNotes] = useState('');
  const [history, setHistory] = useState([]);
  /* Paged CLIENT-side: the fetch above has to pull enough records to find the
   * goals-config, so the rows are already in hand — the page only limits how many
   * past sessions are drawn at once. */
  const [historyPage, setHistoryPage] = useState(1);
  // Row id to focus (the Area combo) right after Add Row.
  const [focusId, setFocusId] = useState(null);
  // Goal rows deleted for THIS session only. The row stays in the standing config
  // (and on the Goals & Targets tab); it is just left out of today's entry.
  const [removedGoalIds, setRemovedGoalIds] = useState([]);

  const reload = useCallback(async () => {
    if (!childId) return;
    try {
      // Network first, the device copy when offline - see utils/dailyActivityOffline.
      const { records } = await loadSheetRecords(childId, DATASHEET_TYPE_CODE);

      // TODAY'S ENTRY, if it was already saved once this day. `records` is sorted
      // newest-first so the first match is the current one. Without this the sheet
      // reopens blank on top of a stored record, and the therapist re-scores a
      // session they already recorded.
      const todayKey = format(new Date(), 'yyyy-MM-dd');
      // THIS SESSION's entry, by session_id. Not "today's", which is a different
      // record as soon as a child is seen twice in one day - the second sheet
      // opened holding the first session's scores. No date fallback while a
      // sessionId is known: a session with nothing saved yet opens empty.
      const isSameDay = (r) => (r?.raw_data?.sessionDate || String(r?.collected_at || '').slice(0, 10)) === todayKey;
      // This session's work saved on the device but not yet synced is newer than
      // anything the server has - it wins, so an offline reload keeps it.
      const localRecord = await getLocalSessionEntry(FORM_TYPES.OT_DATA_SHEET, childId, sessionId);
      const todaysRecord =
        localRecord ||
        records.find(
          (r) => r?.raw_data?.isGoalsConfig !== true && (sessionId ? String(r?.session_id || '') === String(sessionId) : isSameDay(r))
        );
      const todaysGoals = Object.fromEntries((todaysRecord?.raw_data?.goals || []).filter((g) => g?.id).map((g) => [g.id, g]));
      // Only overwrite the box when the stored note has something in it - a reload
      // mid-typing must not wipe what the therapist is part-way through writing.
      if (todaysRecord?.raw_data?.notes) setNotes(todaysRecord.raw_data.notes);
      setRemovedGoalIds(Array.isArray(todaysRecord?.raw_data?.removedGoalIds) ? todaysRecord.raw_data.removedGoalIds : []);

      /* THE ROWS = Goals & Targets as it is now + THIS session's own changes.
       *
       * Nothing a session does is carried to the next one: goals and activities
       * added, removed or edited in a session live on that session's entry only.
       * So a new session always opens with exactly the child's Goals & Targets for
       * this service - never with rows typed into an earlier session, and never
       * with an old copy of a goal's wording or activities. */
      // Older sheets saved a standing goals-config that gave some plan goals their
      // own row id. Reuse it so a session saved back then still lines up with its
      // rows; nothing else is read from it any more.
      const configRecord = records.find((r) => r?.raw_data?.isGoalsConfig === true);
      const idByCode = new Map((configRecord?.raw_data?.goals || []).filter((g) => g?.goalCode && g.id).map((g) => [g.goalCode, g.id]));
      const planGoals = await fetchServiceGoals(childId, SERVICE_CODE);
      const planRows = planGoals.map((pg) => {
        const id = idByCode.get(pg.goalCode) || `iep-${pg.goalCode}`;
        const t = todaysGoals[id];
        return {
          id,
          goalCode: pg.goalCode,
          // A session's own area / goal edit wins for that session only.
          area: t?.sessionEdited ? t.area || '' : pg.area || '',
          goal: t?.sessionEdited ? t.goal || '' : pg.goal,
          sessionEdited: Boolean(t?.sessionEdited),
          activities: pg.activities,
          addedActivities: normalizeActivities(t?.addedActivities),
          skippedActivities: normalizeActivities(t?.skippedActivities),
          promptLevels: toPromptCodeArray(t?.promptLevels),
          performance: t?.performance || '',
          expanded: false
        };
      });
      // Rows added with Add Goal in THIS session, restored from its entry.
      const planIds = new Set(planRows.map((r) => r.id));
      const sessionRows = (todaysRecord?.raw_data?.goals || [])
        .filter((g) => g?.sessionOnly && g.id && !planIds.has(g.id))
        .map((g) => ({
          ...g,
          sessionOnly: true,
          activities: [],
          addedActivities: normalizeActivities(g.addedActivities),
          skippedActivities: [],
          promptLevels: toPromptCodeArray(g.promptLevels),
          expanded: false
        }));
      setGoals([...planRows, ...sessionRows]);

      const historyRecords = records.filter((r) => r?.raw_data?.isGoalsConfig !== true);
      setHistory(historyRecords);
    } catch (err) {
      console.error('[OTDataSheet] reload failed:', err);
    }
  }, [childId, sessionId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // The activities run in this session: the goal's own (from the plan) minus any
  // removed for today, plus any added for today.
  const sessionActivities = (g) => [
    ...normalizeActivities(g.activities).filter((a) => !(g.skippedActivities || []).includes(a)),
    ...normalizeActivities(g.addedActivities)
  ];

  // Shape a goal for persistence / emit — drops the UI-only `expanded` flag and
  // normalises activities to an array. `activities` is what was RUN this session,
  // which is what View Data and history show.
  const sanitizeGoals = (list) =>
    list.map((g) => ({
      id: g.id,
      goalCode: g.goalCode || '',
      seededActivities: Array.isArray(g.seededActivities) ? g.seededActivities : undefined,
      area: g.area,
      goal: g.goal,
      sessionEdited: Boolean(g.sessionEdited),
      activities: sessionActivities(g),
      addedActivities: normalizeActivities(g.addedActivities),
      skippedActivities: normalizeActivities(g.skippedActivities).filter((a) => g.activities.includes(a)),
      sessionOnly: Boolean(g.sessionOnly),
      promptLevels: toPromptCodeArray(g.promptLevels),
      performance: g.performance
    }));

  useEffect(() => {
    if (!onDataChange) return;
    const hasData =
      goals.some((g) => g.performance || g.addedActivities?.length || g.skippedActivities?.length || (g.sessionOnly && g.goal)) ||
      goals.some((g) => g.promptLevels?.length) ||
      removedGoalIds.length > 0 ||
      notes.trim().length > 0;
    if (!hasData) return;
    // No Save button: this auto-save (debounced by the page, flushed at End Session)
    // is the only write path - the old Save also created a second, duplicate entry.
    onDataChange('ot-data-sheet', {
      sessionDate: format(new Date(), 'yyyy-MM-dd'),
      goals: sanitizeGoals(goals.filter((g) => !removedGoalIds.includes(g.id))),
      removedGoalIds,
      notes
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, notes, removedGoalIds]);

  // Goal suggestions per area: the child's known goals, gathered from the current
  // rows and from historical entries. With no area selected, every known goal is
  // offered (build note e).
  const goalPool = useMemo(() => {
    const pool = {};
    const add = (area, goal) => {
      const g = String(goal || '').trim();
      if (!g) return;
      const k = area || '';
      if (!pool[k]) pool[k] = new Set();
      pool[k].add(g);
    };
    goals.forEach((g) => add(g.area, g.goal));
    history.forEach((h) => (h.raw_data?.goals || []).forEach((g) => add(g.area, g.goal)));
    return pool;
  }, [goals, history]);

  const goalOptionsFor = (area) => {
    if (area) return Array.from(goalPool[area] || []);
    const all = new Set();
    Object.values(goalPool).forEach((set) => set.forEach((g) => all.add(g)));
    return Array.from(all);
  };

  // In-session edits change THIS session's rows only - they are never written to
  // the standing goals-config, so the next session opens from Goals & Targets.
  const commitGoals = (next) => setGoals(next);

  const patchRow = (id, patch) => goals.map((g) => (g.id === id ? { ...g, ...patch } : g));

  const changeArea = (id, area) => {
    // Changing the area clears the goal on that row (build note e) - but only
    // a real change: re-picking the same area used to wipe the goal as well.
    const row = goals.find((g) => g.id === id);
    if (!row || row.area === area) return;
    commitGoals(patchRow(id, { area, goal: '', sessionEdited: true }));
  };

  const changeGoal = (id, goal) => commitGoals(patchRow(id, { goal, sessionEdited: true }));

  const toggleExpand = (id) => setGoals((prev) => prev.map((g) => (g.id === id ? { ...g, expanded: !g.expanded } : g)));

  // An activity added in session is for this session only.
  const addActivity = (id, val) =>
    setGoals((prev) => prev.map((g) => (g.id === id ? { ...g, addedActivities: [...(g.addedActivities || []), val] } : g)));

  /* Removing an activity is for THIS session only: the therapist may not run every
   * activity each day. One added this session is simply dropped; one from the
   * goal's own list is left out of today's entry and is back next session. */
  const removeActivity = (id, act) =>
    setGoals((prev) =>
      prev.map((g) => {
        if (g.id !== id) return g;
        const added = g.addedActivities || [];
        const i = added.indexOf(act);
        if (i !== -1) return { ...g, addedActivities: added.filter((_, k) => k !== i) };
        return { ...g, skippedActivities: [...new Set([...(g.skippedActivities || []), act])] };
      })
    );

  // Today's score only — not part of the standing config, so it is not persisted here.
  // Today's prompt level(s) only - like the score, this is the day's data and is
  // NOT written into the standing goals-config.
  const setPromptLevels = (id, value) =>
    setGoals((prev) => prev.map((g) => (g.id === id ? { ...g, promptLevels: toPromptCodeArray(value) } : g)));

  const setPerformance = (id, value) => setGoals((prev) => prev.map((g) => (g.id === id ? { ...g, performance: value } : g)));

  const addRow = () => {
    const id = `g-${Date.now()}`;
    setGoals((prev) => [
      ...prev,
      {
        id,
        goalCode: '',
        area: '',
        goal: '',
        activities: [],
        addedActivities: [],
        skippedActivities: [],
        sessionOnly: true,
        promptLevels: [],
        performance: '',
        expanded: true
      }
    ]);
    setFocusId(id);
  };

  /* Deleting a goal row is for THIS session only - the goal stays on the Goals &
   * Targets tab and comes back next session. A row added with Add Goal exists in
   * this session only, so it is simply dropped. */
  const deleteRow = (id) => {
    const row = goals.find((g) => g.id === id);
    if (!row) return;
    if (row.sessionOnly) {
      setGoals((prev) => prev.filter((g) => g.id !== id));
      return;
    }
    setRemovedGoalIds((prev) => [...new Set([...prev, id])]);
  };

  // The rows on today's entry: everything not deleted for this session.
  const activeGoals = goals.filter((g) => !removedGoalIds.includes(g.id));

  const areaLabel = (key) => AREA_OPTIONS.find((o) => o.key === key)?.label || key || '—';
  const perfLabel = (key) => PERF_OPTIONS.find((o) => o.value === key)?.label || key;

  /* PERFORMANCE — one choice from the fixed assistance scale. Rendered as the same
   * coloured chip the history and View Data screens use, so the reading is identical
   * wherever the entry is looked at later. */

  // A multi-select dropdown rather than a row of buttons: the buttons cost ~210px
  // of a table whose most valuable column is the goal text. Still multi-select -
  // a child can need more than one prompt, same as Cold Probe.
  const renderPromptLevels = (g) => {
    const selected = toPromptCodeArray(g.promptLevels);
    return (
      <FormControl size="small" fullWidth>
        <Select
          multiple
          displayEmpty
          value={selected}
          onChange={(e) => setPromptLevels(g.id, e.target.value)}
          renderValue={(picked) =>
            picked.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.dataSheet.promptLevelPlaceholder', defaultMessage: 'Select' })}
              </Typography>
            ) : (
              // Picked codes as chips, per the mockup - easier to scan than "GP, VP, MP".
              // Two to a row, then the next row.
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: 0.5 }}>
                {picked.map((code) => (
                  <Chip
                    key={code}
                    label={code}
                    size="small"
                    sx={{
                      height: 22,
                      borderRadius: 0.75,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.75 },
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.darker'
                    }}
                  />
                ))}
              </Box>
            )
          }
        >
          {PROMPT_LEVELS.map((p) => (
            <MenuItem key={p.code} value={p.code} dense>
              <Checkbox size="small" checked={selected.includes(p.code)} sx={{ py: 0.25 }} />
              <ListItemText primary={p.code} secondary={p.label} slotProps={{ secondary: { variant: 'caption' } }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderPerformance = (g) => (
    <FormControl size="small" fullWidth>
      <Select
        value={g.performance || ''}
        displayEmpty
        onChange={(e) => setPerformance(g.id, e.target.value)}
        renderValue={(val) =>
          val ? (
            <Chip size="small" color={PERF_COLOR[val]} label={perfLabel(val)} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'form.otDataSheet.performance' })}
            </Typography>
          )
        }
      >
        {PERF_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const historyTotalPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const pagedHistory = history.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);

  return (
    <Box>
      <MainCard sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4">{intl.formatMessage({ id: 'form.otDataSheet.title' })}</Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'form.otDataSheet.subtitle' })}
            </Typography>
          </Box>
        </Stack>
      </MainCard>

      <MainCard sx={{ mb: 2 }} contentSX={{ p: 0 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={intl.formatMessage({ id: 'form.otDataSheet.tab.entry' })} />
          <Tab label={intl.formatMessage({ id: 'form.otDataSheet.tab.history' })} />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: { xs: 1.5, sm: 2 } }} data-swipe-ignore="true">
            <DailyActivityGoalRows
              msgPrefix="form.otDataSheet"
              goals={activeGoals}
              areaOptions={AREA_OPTIONS}
              goalOptionsFor={goalOptionsFor}
              focusId={focusId}
              scoreHeader={intl.formatMessage({ id: 'form.otDataSheet.performance' })}
              scoreWidth={210}
              renderScore={renderPerformance}
              renderPromptLevels={renderPromptLevels}
              onToggleExpand={toggleExpand}
              onChangeArea={changeArea}
              onChangeGoal={changeGoal}
              onAddActivity={addActivity}
              onRemoveActivity={removeActivity}
              onDeleteRow={deleteRow}
              addActivityLabel={intl.formatMessage({ id: 'form.otDataSheet.addActivity', defaultMessage: 'Add' })}
              removeActivityIcon={<CloseOutlined style={{ fontSize: 13 }} />}
            />

            {/* Add Row — appends an empty, expanded row with focus in Area. */}
            <Button
              onClick={addRow}
              fullWidth
              sx={{
                mt: 1.25,
                py: 1.5,
                border: '1px dashed',
                borderColor: 'primary.light',
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                fontWeight: 500,
                '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.06) }
              }}
            >
              {intl.formatMessage({ id: 'form.otDataSheet.addRow', defaultMessage: '+ Add Row' })}
            </Button>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {intl.formatMessage({ id: 'form.otDataSheet.notes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={intl.formatMessage({ id: 'form.otDataSheet.notesPlaceholder' })}
              />
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            {history.length === 0 ? (
              <HistoryEmptyState message={intl.formatMessage({ id: 'form.otDataSheet.history.empty' })} />
            ) : (
              <Stack spacing={2}>
                {pagedHistory.map((h) => {
                  const rd = h.raw_data || {};
                  const dateStr = rd.sessionDate || (h.collected_at ? h.collected_at.split('T')[0] : '—');
                  const histGoals = Array.isArray(rd.goals) ? rd.goals.filter((g) => g.performance) : [];
                  // Notes are persisted on the parent `notes` column, not raw_data — fall back to it.
                  const histNotes = rd.notes || h.notes;
                  return (
                    <MainCard key={h.id} contentSX={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {dateStr}
                      </Typography>
                      <Grid container spacing={1}>
                        {histGoals.map((g, i) => (
                          <Grid size={{ xs: 12, md: 6 }} key={g.id || i}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={areaLabel(g.area)} />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {g.goal}
                              </Typography>
                              {toPromptCodeArray(g.promptLevels).map((c) => (
                                <Chip
                                  key={c}
                                  size="small"
                                  variant="outlined"
                                  label={c}
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                                />
                              ))}
                              <Chip size="small" color={PERF_COLOR[g.performance] || 'default'} label={perfLabel(g.performance)} />
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                      {histNotes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {histNotes}
                        </Typography>
                      )}
                    </MainCard>
                  );
                })}
              </Stack>
            )}
            <HistoryPagination
              page={historyPage}
              totalPages={historyTotalPages}
              total={history.length}
              pageSize={HISTORY_PAGE_SIZE}
              onChange={setHistoryPage}
            />
          </Box>
        )}
      </MainCard>
    </Box>
  );
};

OTDataSheetMode.propTypes = {
  onDataChange: PropTypes.func,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  child: PropTypes.object
};

export default OTDataSheetMode;
